import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BrandData } from '../shared/brand-data';
import { BestService } from './best.service';
import { Meta } from '@angular/platform-browser';
import { isDevMode } from '@angular/core';
import { SUBMIT_DRY_RUN } from './submit-dry-run';

export const FREERATEUPDATE_LOGO_SVG = '/assets/images/freerateupdate-logo.svg';

/** FRU-23491: the brand domain for this page. The fixture build stands in for the live site (SUBMIT_DRY_RUN.brandDomain);
 *  every real build has no dry run, so the page's own host decides. */
export function brandDomainFor(rawHostname: string, dryRun: { brandDomain?: string } | null): string {
  return (dryRun && dryRun.brandDomain) || brandDomainFromHostname(rawHostname);
}

export function brandDomainFromHostname(rawHostname: string): string {
  const hostname = (rawHostname || '').toLowerCase().replace(/\.+$/, '');
  const splitDomain = hostname.split('.').filter(Boolean);
  return splitDomain.length > 2
    ? splitDomain.slice(-2).join('.')
    : hostname;
}

@Injectable({
  providedIn: 'root'
})
export class BrandService {
  data: BrandData;

  constructor(private route: ActivatedRoute, private best: BestService) {
    this.data = new BrandData;

    const prettyDomains = {
      'top10bestlenders.com': 'Top10BestLenders',
      'lowratefinder.ai': 'LowRateFinder',
      'airatefinder.com': 'AiRateFinder',
    };

    // Get the domain without subdomain
    const hostname = window.location.hostname.toLowerCase().replace(/\.+$/, '');
    let brand = this.route.snapshot.queryParams['brand'];
    this.data.domain = brandDomainFor(hostname, SUBMIT_DRY_RUN);
    const pathname = window.location.pathname;

    switch (this.data.domain) {
      case 'homeequitychoice.com':
        this.data.pageType = 'home-equity-loan';
        this.data.view = 'fru';
        this.data.trackingSrc = '/assets/scripts/form-landing-tracking-fru.js';
        this.data.logoSrc = '/assets/images/homeequitychoice.png';
        break;
      
      case 'besthomeequityloan.com':
        this.data.pageType = 'home-equity-loan';
        this.data.view = 'fru';
        this.data.trackingSrc = '/assets/scripts/form-landing-tracking-bhel.js';
        this.data.logoSrc = '/assets/images/besthomeequityloan.png';
        break;

      case 'lowratefinder.ai':
      case 'airatefinder.com':
        if (pathname.includes('/communication/home-equity')
          || window.location.search.includes('type=home-equity-loan')) {
          this.data.pageType = 'home-equity-loan';
        }
        this.data.prettyDomain = prettyDomains[this.data.domain] || 'LowRateFinder';
        this.data.view = 'lrf';
        this.data.trackingSrc = '/assets/scripts/form-landing-tracking-fru.js';
        this.data.logoSrc = 'assets/images/lrf-logo.webp';
        this.data.logoBottomSrc = 'assets/images/lrf-logo-with-shadow.webp';
        break;

      default:
        if (pathname.includes('/communication/home-equity')
        || pathname.includes('/home-equity-ai')
        || window.location.search.includes('type=home-equity-loan')
      ) {
          this.data.pageType = 'home-equity-loan';
        }
        this.data.view = 'fru';
        this.data.trackingSrc = '/assets/scripts/form-landing-tracking-fru.js';
        this.data.logoSrc = '/assets/images/fru-logo.svg';
        break;
    }

    if (brand === 'best') {
      this.data.view = 'best';

      if (this.data.abLogoSrc == '') {
        this.data.abLogoSrc = this.data.logoSrc;
      }

      this.best.lenderSubscribe.subscribe((clientId) => {
        this.data.logoSrc = (clientId === '')
        ? '/assets/images/logo.png'
        : `https://s3-us-west-2.amazonaws.com/fru-client-logos/production/300x100/${clientId}.png`;

        this.data.logoWasabiSrc = this.data.logoSrc.replace('s3-us-west-2.amazonaws.com', 's3.us-central-1.wasabisys.com');
      });
    }
    if (isDevMode()) {
      console.log('BrandService calculation', this.data);
    }
  }

  updateMetaDescription(meta: Meta): void {
    const prettyDomain = this.data.prettyDomain || 'FreerateUpdate';
    const currentDescription = meta.getTag('name=description')?.content || '';
    const regex = new RegExp(` from ${prettyDomain}.`, 'g');
    const cleanDescription = currentDescription.replace(regex, '');
    const description = `${cleanDescription} from ${prettyDomain}.`;
    meta.updateTag({ name: 'description', content: description });
  }

  get isTop10(): boolean {
    return this.data.domain.includes('top10');
  }

  get pageType(): string {
    return this.data.pageType;
  }

  set pageType(pt: string) {
    this.data.pageType = pt;
  }

  get view(): string {
    return this.data.view;
  }

  set view(v: string) {
    this.data.view = v;
  }

  get trackingSrc(): string {
    return this.data.trackingSrc;
  }

  set trackingSrc(ts: string) {
    this.data.trackingSrc = ts;
  }

  get abLogoSrc(): string {
    return this.data.abLogoSrc;
  }

  set abLogoSrc(ls: string) {
    this.data.abLogoSrc = ls;
  }

  get logoSrc(): string {
    return this.data.logoSrc;
  }

  set logoSrc(ls: string) {
    this.data.logoSrc = ls;
  }

  get tyLogoSrc(): string {
    return this.data.tyLogoSrc;
  }

  get logoWasabiSrc(): string {
    return this.data.logoWasabiSrc;
  }

  get domain(): string {
    return this.data.domain;
  }

  set domain(d: string) {
    this.data.domain = d;
  }
}
